/**
 * `GET backrooms/{id}` used to return the result file as `resultFilePath` (a
 * relative key) and now returns `resultFileUrl` (a full S3 URL) instead. The
 * rename is intentional on the BE side; these helpers keep the screens working
 * against either shape, so a lagging environment does not break the UI.
 *
 * Note `resultFilePath` is ALSO the key the upload form uses to hold a locally
 * picked `File` before it is sent, so a module can carry either a File (not yet
 * uploaded) or a URL string (already stored).
 */

/** The module's result file — a URL string from the API, or a local `File`. */
export const getResultFile = (module) =>
  module?.resultFileUrl || module?.resultFilePath || null;

/** Whether the module has a result file at all. */
export const hasResultFile = (module) => Boolean(getResultFile(module));

/**
 * Reduce either shape to the S3 object key, so a full URL can be compared with
 * a stored path. Percent-escapes and the "+"-for-space convention are undone so
 * the two sides spell a space the same way.
 */
export const toResultFileKey = (value) => {
  if (!value || typeof value !== 'string') return '';
  let path = value;
  try {
    path = new URL(value).pathname;
  } catch (e) {
    // Not an absolute URL - already a path.
  }
  try {
    return decodeURIComponent(path.replace(/^\//, '').replace(/\+/g, '%20'));
  } catch (e) {
    return path.replace(/^\//, '');
  }
};

/** True when a module and a review item point at the same stored file. */
export const isSameResultFile = (a, b) => {
  const keyA = toResultFileKey(a);
  const keyB = toResultFileKey(b);
  return Boolean(keyA) && keyA === keyB;
};
